import { ChevronRight, Clock } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';

type Jurisdiction = 'ru' | 'eu' | 'us';

const JURISDICTION_LABEL: Record<Jurisdiction, string> = {
  ru: 'РФ',
  eu: 'ЕС',
  us: 'США',
};

interface Props {
  title: string;
  description: string;
  href: string;
  readingMinutes: number;
  icon: LucideIcon;
  jurisdictions?: Jurisdiction[];
}

/**
 * Карточка документа на index-странице /legal.
 * Использует только design tokens (Q094/Q096) — никаких hardcoded hex.
 * Tag <a> — native a11y + right-click open-in-new-tab works.
 */
export function DocumentCard({
  title,
  description,
  href,
  readingMinutes,
  icon: Icon,
  jurisdictions,
}: Props) {
  return (
    <a
      href={href}
      className="group block rounded-xl border border-border bg-surface-canvas p-6 hover:border-action-primary-bg hover:bg-surface-subtle focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus/40 transition-all"
    >
      <div className="flex items-start gap-4">
        <div className="shrink-0 rounded-lg bg-action-primary-bg/10 p-3 group-hover:bg-action-primary-bg/15 transition-colors">
          <Icon className="h-6 w-6 text-action-primary-bg" aria-hidden />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-lg font-semibold text-text-primary md:text-xl">{title}</h2>
          <p className="mt-2 text-sm leading-relaxed text-text-secondary">{description}</p>
          <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-text-secondary">
            <span className="inline-flex items-center gap-1.5">
              <Clock className="h-3.5 w-3.5" aria-hidden />
              {readingMinutes} мин
            </span>
            {jurisdictions && jurisdictions.length > 0 && (
              <>
                <span aria-hidden>·</span>
                <span>{jurisdictions.map((j) => JURISDICTION_LABEL[j]).join(' · ')}</span>
              </>
            )}
          </div>
        </div>
        <ChevronRight
          className="shrink-0 h-5 w-5 text-text-secondary group-hover:text-action-primary-bg transition-colors"
          aria-hidden
        />
      </div>
    </a>
  );
}
